import {
  getGetFrontAreaPubblicaRichiesteIdModuliQueryOptions,
  getGetFrontRichiesteIdModuliQueryOptions,
  useGetQuery,
} from '@framework/api-client';
import produce from 'immer';
import { sortBy } from 'lodash';
import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { isPublicSubmission } from '../isPublicSubmission';

export interface IUseSubmissionModulesParams {
  submissionId: string | null | undefined;
}

/**
 * Ritorna l'API corretta da chiamare in base al path fornito.
 * Nel caso di un path che contiene 'osservazioni' ritorna l'endpoint per le richieste pubbliche.
 * Altrimenti, ritorna l'endpoint per le richieste dell'utente.
 */
function getSubmissionModuleEndpoint(path: string) {
  if (isPublicSubmission(path)) {
    // GET/area-pubblica/richieste/{id}/moduli
    return getGetFrontAreaPubblicaRichiesteIdModuliQueryOptions;
  }

  // GET/richesta/{id}/moduli
  return getGetFrontRichiesteIdModuliQueryOptions;
}

/**
 * Ritorna la richiessta in base all'id fornito, richiamando l'API
 * "privata" se si sta guardando una propria richiesta, o quella "pubblica"
 * se si cerca una richiesta pubblica.
 */
function useSubmissionModulesById(submissionId: string | null | undefined) {
  const location = useLocation();

  const queryOptions = useMemo(
    () => getSubmissionModuleEndpoint(location.pathname),
    [location.pathname],
  );

  const result = useGetQuery(queryOptions, [
    submissionId!,
    { query: { enabled: Boolean(submissionId) } },
  ]);

  return result;
}

export type ISubmissionModules = ReturnType<typeof useSubmissionModules>;

export function useSubmissionModules(params: IUseSubmissionModulesParams) {
  const { submissionId } = params;

  const { data, isLoading, isError, error } =
    useSubmissionModulesById(submissionId);

  const modules = useMemo(() => {
    if (!data) return [];
    return produce(data, draft => {
      // Riordino i moduli
      draft?.forEach(module => {
        if (!module.modulo?.sezioni) return;
        module.modulo.sezioni = sortBy(module.modulo.sezioni, 'ordine');
      });

      return;
    });
  }, [data]);

  return { modules, isLoading, isError, error };
}
